import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Box,
  Paper,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import axios from 'axios';
import config from '../../config';
import { persistentStorage } from '../../utils/storage';
import { markSessionStart } from '../../utils/session';

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
    padding: '20px',
  },
  card: {
    width: '100%',
    maxWidth: 400,
    padding: '32px',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  title: {
    fontWeight: 700,
    fontSize: '28px',
    color: '#C084FC',
    marginBottom: '8px',
  },
  subtitle: {
    color: '#B6B6B6',
    fontSize: '14px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  button: {
    background: 'linear-gradient(180deg, #724F96 0%, #C084FC 100%)',
    color: 'white',
    padding: '14px',
    borderRadius: '8px',
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '16px',
    '&:hover': {
      background: 'linear-gradient(180deg, #5a3d77 0%, #a06dd4 100%)',
    },
    '&:disabled': {
      background: '#ccc',
    },
  },
  link: {
    textAlign: 'center',
    marginTop: '20px',
    color: '#B6B6B6',
    fontSize: '14px',
    '& a': {
      color: '#C084FC',
      textDecoration: 'none',
      fontWeight: 500,
    },
  },
}));

const EventOrganizerLogin = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/agency/login`,
        { email, password }
      );

      if (response.data && response.data.agency) {
        const agency = response.data.agency;
        
        // Store agency data (backend uses agency_id)
        await persistentStorage.set('currentAgencyId', agency.agency_id);
        await persistentStorage.set('currentAgencyName', agency.name);
        await persistentStorage.set('currentRole', 'agency');
        await persistentStorage.set('currentAgency', JSON.stringify(agency));
        
        // Mark session start (TTL)
        await markSessionStart();
        
        showSnackbar('Login successful!', 'success');
        
        // Navigate to event organizer dashboard
        setTimeout(() => {
          navigate('/event-organizer/dashboard');
        }, 500);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Login failed. Please try again.';
      setError(errorMsg);
      showSnackbar(errorMsg, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={classes.page}>
      <Paper className={classes.card} elevation={0}>
        <Box className={classes.header}>
          <Typography className={classes.title}>Event Organizer Login</Typography>
          <Typography className={classes.subtitle}>Sign in to manage your events</Typography>
        </Box>

        <form onSubmit={handleLogin} className={classes.form}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="outlined"
            required
            disabled={isLoading}
          />

          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            variant="outlined"
            required
            disabled={isLoading}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            className={classes.button}
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
          </Button>
        </form>

        <Box className={classes.link}>
          <Typography>
            Don't have an account? <Link to="/event-organizer/signup">Sign up</Link>
          </Typography>
          <Typography sx={{ mt: 2, fontSize: '13px', color: '#B6B6B6' }}>
            Other logins: {' '}
            <Link to="/login" style={{ color: '#C084FC', textDecoration: 'none', marginRight: '8px' }}>Customer</Link>
            <span style={{ color: '#B6B6B6' }}>|</span>
            <Link to="/associates-login" style={{ color: '#C084FC', textDecoration: 'none', margin: '0 8px' }}>Associates</Link>
            <span style={{ color: '#B6B6B6' }}>|</span>
            <Link to="/valet-service/login" style={{ color: '#C084FC', textDecoration: 'none', marginLeft: '8px' }}>Valet Service</Link>
          </Typography>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default EventOrganizerLogin;

